import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faGripLines } from '@fortawesome/free-solid-svg-icons';
import type { AnnotationLinkStyle } from 'balus-common';
import { AnnotationLinkStyles } from 'balus-common';
import { MainMenuIcon, MenuContainer, SubMenuListContainer, TrashMenuButton } from '@model-framework/menu';
import { MainMenuBox, SubMenuBox } from '@model-framework/menu/MenuBox';
import { ReverseLinkButton } from '@model-framework/link';
import { Point } from '@view-model/models/common/basic';
import { MenuSize } from '@view-model/models/sticky/ui';
import { AnnotationLinkShape } from './AnnotationLinkShape';
import { straightBezier } from './straightBezier';
import { AnnotationLinkStyleI18nKeys } from './AnnotationLinkStyleI18nKeys';

type Props = {
  position: Point;
  currentStyle: AnnotationLinkStyle;
  onStyleSelected(style: AnnotationLinkStyle): void;
  onReverse(): void;
  onDelete(): void;
};

export const AnnotationLinkMenu: React.FC<Props> = ({
  position,
  currentStyle,
  onStyleSelected,
  onReverse,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const [isStyleMenuOpen, setIsStyleMenuOpen] = useState(false);
  return (
    <MenuContainer position={position} size={{ width: MenuSize * 3, height: MenuSize }} positionOrigin="center-bottom">
      <MainMenuBox
        menuSize={MenuSize}
        isSelected={isStyleMenuOpen}
        onClick={() => setIsStyleMenuOpen((open) => !open)}
        text={t('canvas.menu.lineStyle')}
      />
      <MainMenuIcon icon={faGripLines} menuSize={MenuSize} />
      {isStyleMenuOpen && (
        <SubMenuListContainer
          menuSize={MenuSize}
          rectSize={{ width: MenuSize * AnnotationLinkStyles.length, height: MenuSize }}
        >
          {AnnotationLinkStyles.map((style, i) => {
            const isSelected = style === currentStyle;
            return (
              <g key={style} transform={`translate(${MenuSize * i}, 0)`} onClick={() => onStyleSelected(style)}>
                <title>{t(AnnotationLinkStyleI18nKeys[style])}</title>
                <SubMenuBox isSelected={isSelected} menuSize={MenuSize} />
                <g pointerEvents="none">
                  <AnnotationLinkShape
                    style={style}
                    bezier={straightBezier(
                      new Point(MenuSize / 2 - 12, MenuSize / 2),
                      new Point(MenuSize / 2 + 12, MenuSize / 2)
                    )}
                    reversed={false}
                    color={isSelected ? 'white' : '#aaa'}
                    thickness={2}
                  />
                </g>
              </g>
            );
          })}
        </SubMenuListContainer>
      )}
      <g transform={`translate(${MenuSize}, 0)`}>
        <ReverseLinkButton menuSize={MenuSize} onClick={onReverse} text={t('canvas.menu.reverseLink')} />
      </g>
      <g transform={`translate(${MenuSize * 2}, 0)`}>
        <TrashMenuButton menuSize={MenuSize} onClick={onDelete} text={t('common.action.delete')} />
      </g>
    </MenuContainer>
  );
};
